/**
 * Minimal analytics abstraction.
 *
 * Emits named events to whichever provider is configured via env
 * (GA4 gtag or PostHog). Falls back to console in dev. Designed so a
 * provider can be wired later without changing call sites.
 */

export type AnalyticsEvent =
  | 'collections_landing_view'
  | 'collections_cta_click'
  | 'collections_wizard_opened'
  | 'collections_form_started'
  | 'collections_step_view'
  | 'collections_invoicing_platform_selected'
  | 'collections_payment_method_selected'
  | 'collections_pain_selected'
  | 'collections_price_seen'
  | 'collections_price_commitment_selected'
  | 'collections_lead_submitted'
  | 'collections_form_error';

type Props = Record<string, unknown>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    posthog?: { capture: (event: string, props?: Props) => void };
    dataLayer?: unknown[];
  }
}

const provider = (import.meta.env.VITE_ANALYTICS_PROVIDER || '').toLowerCase();
const isDev = import.meta.env.DEV;

let initialized = false;

function loadGa4(measurementId: string) {
  if (!measurementId || document.getElementById('ga4-src')) return;
  const s = document.createElement('script');
  s.id = 'ga4-src';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
}

export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  if (provider === 'ga4') {
    loadGa4(import.meta.env.VITE_GA4_MEASUREMENT_ID || '');
  }
  // PostHog is expected to be initialized by a snippet in index.html when used;
  // here we only call window.posthog.capture if present.
}

export function track(event: AnalyticsEvent, props: Props = {}): void {
  if (typeof window === 'undefined') return;
  try {
    if (provider === 'ga4' && typeof window.gtag === 'function') {
      window.gtag('event', event, props);
    } else if (provider === 'posthog' && window.posthog) {
      window.posthog.capture(event, props);
    }
    if (isDev) {
      console.debug('[analytics]', event, props);
    }
  } catch {
    // Never let analytics break the page.
  }
}
